import { isClient, safeLocalStorage, safeSessionStorage } from '@/composables/useSSR'
import { useMatomo } from '@/composables/useMatomo'
import { appConfig } from 'config/app'

const GCLID_STORAGE_KEY = 'gclid'
const GCLID_TIMESTAMP_KEY = 'gclid_timestamp'
const ATTRIBUTION_STORAGE_KEY = 'ad_attribution'
const ATTRIBUTION_TIMESTAMP_KEY = 'ad_attribution_timestamp'
const GCLID_VALIDITY_DAYS = 90

const TRACKED_PARAMS = [
  'gclid',
  'gad_source',
  'gad_campaignid',
  'gbraid',
  'wbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content'
] as const

export type AttributionParams = Partial<Record<(typeof TRACKED_PARAMS)[number], string>>

let gtagLoaded = false

/**
 * Campaign parameters read from the landing URL but not yet written anywhere.
 *
 * § 25 Abs. 1 TDDDG requires consent before storing information in, or reading
 * it from, the user's terminal equipment. It is technology-neutral, so
 * localStorage is covered just as cookies are, and the § 25 Abs. 2 Nr. 2
 * necessity exemption does not stretch to advertising attribution. This module
 * previously wrote the parameters on load, before the banner had even
 * rendered — meaning a visitor who then chose "only necessary" already had the
 * attribution on their device.
 *
 * Holding them in memory keeps the campaign data available for the length of
 * the page visit without touching storage, so nothing is lost if consent
 * arrives moments later, and nothing is retained if it never does.
 */
let pendingAttribution: AttributionParams = {}

const readAttributionFromUrl = (): AttributionParams => {
  if (!isClient) return {}

  const urlParams = new URLSearchParams(window.location.search)
  const captured: AttributionParams = {}
  for (const key of TRACKED_PARAMS) {
    const value = urlParams.get(key)
    if (value) captured[key] = value
  }
  return captured
}

const writeAttribution = (params: AttributionParams): void => {
  const storage = safeLocalStorage()
  if (!storage || Object.keys(params).length === 0) return

  const now = Date.now().toString()
  storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(params))
  storage.setItem(ATTRIBUTION_TIMESTAMP_KEY, now)

  // Keep legacy gclid keys in sync so anything still reading them continues to work.
  if (params.gclid) {
    storage.setItem(GCLID_STORAGE_KEY, params.gclid)
    storage.setItem(GCLID_TIMESTAMP_KEY, now)
  }
}

/**
 * Capture campaign parameters into memory. Safe to call before consent —
 * nothing is written to the device until `persistAttribution()` runs.
 */
export function captureAttribution(): void {
  if (!isClient) return
  const captured = readAttributionFromUrl()
  if (Object.keys(captured).length > 0) {
    pendingAttribution = captured
  }
}

/** Persist captured parameters. Call only once marketing consent exists. */
export function persistAttribution(): void {
  writeAttribution(pendingAttribution)
}

/**
 * Remove everything this module stored on the device.
 *
 * Art. 7 Abs. 3 DSGVO requires withdrawing consent to be as easy as giving it,
 * which a withdrawal that leaves the identifiers in place does not satisfy.
 */
export function clearAttribution(): void {
  pendingAttribution = {}
  const storage = safeLocalStorage()
  if (!storage) return
  storage.removeItem(ATTRIBUTION_STORAGE_KEY)
  storage.removeItem(ATTRIBUTION_TIMESTAMP_KEY)
  storage.removeItem(GCLID_STORAGE_KEY)
  storage.removeItem(GCLID_TIMESTAMP_KEY)
}

/**
 * Stop gtag from sending anything further.
 *
 * An injected script cannot be truly unloaded, so this uses Google's own
 * documented opt-out flag, which gtag checks before every hit. Without it,
 * tracking continued for the rest of the page session after withdrawal.
 */
export function disableGtag(): void {
  if (!isClient) return
  const gtagId = appConfig.env.gtagId
  if (gtagId) {
    ;(window as unknown as Record<string, unknown>)[`ga-disable-${gtagId}`] = true
  }
  gtagLoaded = false
}

// Read into memory on load; persisted only once consent is given.
if (isClient) {
  captureAttribution()
}

export function fireCheckoutConversions(): void {
  if (!isClient) return
  const storage = safeSessionStorage()
  if (!storage) return
  const metaRaw = storage.getItem('checkout_conversion_meta')
  storage.removeItem('checkout_conversion_meta')
  const meta = metaRaw ? JSON.parse(metaRaw) : null

  // Matomo purchase conversion (consent-independent, unlike the Google Ads
  // pixels below). Fires on every paid checkout regardless of product, so a
  // single Matomo "purchase" goal captures all paid conversions.
  useMatomo().trackEvent('Conversion', 'purchase')

  const { trackConversion } = useGoogleAds()

  if (appConfig.env.gadsConversionPayment) {
    trackConversion(appConfig.env.gadsConversionPayment)
  }

  if (meta?.hasK8s && appConfig.env.gadsConversionK8s) {
    trackConversion(appConfig.env.gadsConversionK8s, 50)
  }

  // VServer purchase. Distinct from the generic payment conversion so the
  // optimizer can score the VServer funnel separately. Value (~€15) is a
  // conservative first-month-revenue estimate; tune in the Google Ads UI once
  // there's real VServer cohort data.
  if (meta?.hasVServer && appConfig.env.gadsConversionVserver) {
    trackConversion(appConfig.env.gadsConversionVserver, 15)
  }
}

export function useGoogleAds() {
  const getStoredAttribution = (): AttributionParams => {
    const storage = safeLocalStorage()
    if (!storage) return {}

    const raw = storage.getItem(ATTRIBUTION_STORAGE_KEY)
    const timestamp = storage.getItem(ATTRIBUTION_TIMESTAMP_KEY)

    if (!raw || !timestamp) return {}

    const storedTime = parseInt(timestamp, 10)
    const validityMs = GCLID_VALIDITY_DAYS * 24 * 60 * 60 * 1000
    const isValid = Date.now() - storedTime < validityMs

    if (!isValid) {
      storage.removeItem(ATTRIBUTION_STORAGE_KEY)
      storage.removeItem(ATTRIBUTION_TIMESTAMP_KEY)
      storage.removeItem(GCLID_STORAGE_KEY)
      storage.removeItem(GCLID_TIMESTAMP_KEY)
      return {}
    }

    try {
      const parsed = JSON.parse(raw) as AttributionParams
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }

  const getStoredGclid = (): string | null => {
    return getStoredAttribution().gclid ?? null
  }

  const loadGtag = (): void => {
    if (!isClient || gtagLoaded) return

    const gtagId = appConfig.env.gtagId
    if (!gtagId) return

    // Clear a previous opt-out: the flag persists on `window` for the page
    // session, so re-consenting after a withdrawal would otherwise load the
    // script into a state where every hit is silently suppressed.
    delete (window as unknown as Record<string, unknown>)[`ga-disable-${gtagId}`]

    // Inject gtag.js script
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`
    document.head.appendChild(script)

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || []
    window.gtag = function (...args: unknown[]) {
      window.dataLayer.push(args)
    }
    window.gtag('js', new Date())
    window.gtag('config', gtagId)

    gtagLoaded = true
  }

  const isGtagLoaded = (): boolean => gtagLoaded

  const trackConversion = (label: string, value?: number, currency = 'EUR'): void => {
    if (!isClient || !gtagLoaded || !window.gtag) return

    const params: Record<string, unknown> = {
      send_to: label
    }
    if (value !== undefined) {
      params.value = value
      params.currency = currency
    }
    window.gtag('event', 'conversion', params)
  }

  return {
    loadGtag,
    getStoredGclid,
    getStoredAttribution,
    isGtagLoaded,
    trackConversion
  }
}
